import React from "react";

/**
 * Local fallback icons for Priest service categories.
 *
 * The category images shown in the sidebar normally come from the backend
 * (`category.image_id` -> `${config.API_BASE_URL}/gallery/download/${imageid}`).
 * When a category has no image_id set on the backend, the UI used to fall
 * back to "Desktop placeholder.svg", which is a blank white square — that's
 * why categories like Funeral, Kids OR Newborn, Pooja, Social Gathering,
 * Wedding and Women were showing up as empty circles in the dashboard even
 * though they display correctly with icons in the app.
 *
 * These icons are shown ONLY when the backend hasn't provided an image for
 * a category, so the sidebar always shows something meaningful instead of
 * a blank circle.
 */

const iconWrapStyle = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#FFF5F0",
};

const AnniversaryIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <rect x="6" y="10" width="36" height="30" rx="4" fill="#F7735B" />
    <rect x="6" y="10" width="36" height="9" rx="3" fill="#B94A36" />
    <text x="24" y="33" textAnchor="middle" fontSize="16" fill="#fff" fontFamily="Arial" fontWeight="bold">1</text>
  </svg>
);

const BirthdayIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <rect x="10" y="24" width="28" height="16" rx="3" fill="#8B4A2B" />
    <rect x="10" y="18" width="28" height="8" fill="#C17A4A" />
    <rect x="21" y="8" width="6" height="12" fill="#F2C879" />
    <circle cx="24" cy="7" r="2.5" fill="#F7735B" />
  </svg>
);

const CorporateIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <rect x="7" y="18" width="34" height="20" rx="3" fill="#8B5E34" />
    <rect x="17" y="12" width="14" height="8" rx="2" fill="none" stroke="#8B5E34" strokeWidth="3" />
    <rect x="7" y="26" width="34" height="4" fill="#6E4728" />
  </svg>
);

const FestivalIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <ellipse cx="24" cy="34" rx="16" ry="6" fill="#8B4A2B" />
    <path d="M12 34 Q24 22 36 34 Z" fill="#C17A4A" />
    <path d="M24 22 C22 17 25 13 24 8 C27 12 29 17 24 22 Z" fill="#F2A03D" />
  </svg>
);

const FuneralIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <path d="M10 30 C10 22 16 16 24 16 C32 16 38 22 38 30 Z" fill="#B08968" />
    <rect x="8" y="30" width="32" height="5" rx="2" fill="#8B6A4E" />
    <path d="M24 16 C22 12 25 9 24 5 C27 8 28 12 24 16 Z" fill="#F2A03D" />
  </svg>
);

const KidsNewbornIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <circle cx="24" cy="22" r="13" fill="#F2C879" />
    <circle cx="19" cy="21" r="1.8" fill="#6E4728" />
    <circle cx="29" cy="21" r="1.8" fill="#6E4728" />
    <path d="M18 27 Q24 31 30 27" stroke="#6E4728" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M14 14 Q24 6 34 14" stroke="#C17A4A" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

const PoojaIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <ellipse cx="24" cy="33" rx="17" ry="5" fill="#C17A4A" />
    <ellipse cx="24" cy="30" rx="13" ry="3.5" fill="#F2A03D" />
    <path d="M24 27 C22 22 25 18 24 12 C27 16 29 21 24 27 Z" fill="#F7735B" />
  </svg>
);

const SocialGatheringIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <circle cx="16" cy="17" r="6" fill="#C17A4A" />
    <circle cx="32" cy="17" r="6" fill="#F2A03D" />
    <path d="M6 38 C6 29 12 25 16 25 C20 25 26 29 26 38 Z" fill="#C17A4A" />
    <path d="M22 38 C22 29 28 25 32 25 C36 25 42 29 42 38 Z" fill="#F2A03D" />
  </svg>
);

const WeddingIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <path d="M10 40 V18 Q10 8 24 8 Q38 8 38 18 V40" fill="none" stroke="#8B5E34" strokeWidth="4" />
    <rect x="6" y="38" width="36" height="4" fill="#8B5E34" />
  </svg>
);

const WomenIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <path d="M24 8 C17 8 13 14 14 21 C14.5 26 17 29 19 31 L18 40 L30 40 L29 31 C31 29 33.5 26 34 21 C35 14 31 8 24 8 Z" fill="#F2C879" />
    <path d="M14 21 C13 15 17 9 24 9 C31 9 35 15 34 21" fill="#6E4728" />
  </svg>
);

const AddNewIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <circle cx="24" cy="24" r="16" fill="none" stroke="#8B5E34" strokeWidth="3" />
    <path d="M24 16 V32 M16 24 H32" stroke="#8B5E34" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const DefaultCategoryIcon = () => (
  <svg viewBox="0 0 48 48" width="60%" height="60%">
    <rect x="8" y="8" width="14" height="14" rx="3" fill="#C17A4A" />
    <rect x="26" y="8" width="14" height="14" rx="3" fill="#F2A03D" />
    <rect x="8" y="26" width="14" height="14" rx="3" fill="#F2A03D" />
    <rect x="26" y="26" width="14" height="14" rx="3" fill="#C17A4A" />
  </svg>
);

// Map normalized category names -> icon component
const ICON_MAP = {
  "anniversary": AnniversaryIcon,
  "birthday": BirthdayIcon,
  "corporate or business": CorporateIcon,
  "festival": FestivalIcon,
  "funeral": FuneralIcon,
  "kids or newborn": KidsNewbornIcon,
  "pooja": PoojaIcon,
  "social gathering": SocialGatheringIcon,
  "wedding": WeddingIcon,
  "women": WomenIcon,
  "package": AddNewIcon,
};

const normalize = (name) => (name || "").trim().toLowerCase();

/**
 * Returns a fallback icon element for a given category name.
 * Falls back to a generic category icon if no specific match is found.
 */
export function CategoryFallbackIcon({ categoryName }) {
  const key = normalize(categoryName);
  const IconComponent = ICON_MAP[key] || DefaultCategoryIcon;
  return (
    <div style={iconWrapStyle}>
      <IconComponent />
    </div>
  );
}

export default CategoryFallbackIcon;